import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { ConfigurationService } from '../configuration/configuration.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { PortalPreferencesInterface } from '../../interfaces/portal-preferences.interface';
import { RestApiService } from '../rest-api/rest-api.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class PortalConfigurationService {
    public get contractCodeMapping() {
        return {
            partnerContractCode: {
                'at-ext-sd': 'ATMT',
                'au-ext-sd': 'AUST',
                'ca-ext-md': 'CA00',
                'ca-ext-sd': 'CA00',
                'ch-ext-md': 'CHMT',
                'ch-ext-sd': 'CHMT',
                'csa-scp-md': 'DEST',
                'csa-scp-sd': 'DEST',
                'csa-ext-sd': 'DEST',
                'cz-ext-md': 'CZST',
                'cz-ext-sd': 'CZST',
                'de-ext': 'DEFAULT',
                'de-ext-lanxess': 'DEST',
                'de-ext-md': 'DEMT',
                'de-ext-rss': 'DEST',
                'de-ext-sd': 'DEST',
                'de-scp': 'DESCP',
                'de-scp-sd': 'DESCP',
                'de-scp-sd-bs': 'DEST',
                'de-scp-sd-ind': 'DESCP',
                'de-ext-bkk': 'DEST',
                'dk-ext-sd': 'DK00',
                'es-ext-md': 'ESMT',
                'es-ext-sd': 'ESMT',
                'es-scp-sd': 'ESMT',
                'fi-ext-sd': 'FI00',
                'mea-ext-md': 'AE00',
                'mea-ext-sd': 'AE00',
                'mea-scp': 'DEST',
                'nrd-scp': 'UKST',
                'no-ext': 'NOST',
                'se-ext-sd': 'SE00',
                'test-cs': 'DEFAULT',
                'uk-ext-md': 'UKST',
                'uk-ext-sd': 'UKST',
                'uk-scp-sd': 'UKST',
                'us-ext-md': 'USST',
                'us-ext-sd': 'USST',
                'us-scp-pp': 'USST',
                'ww-ext': 'DEFAULT',
                'ww-scp-md': 'DEMT',
                'ww-scp-sd': 'DEST',
                'za-ext-md': 'ZA00',
                'aaa-scp': 'DEFAULT',
                'id-scp-sd': 'DEFAULT',
                'id-scp-md': 'DEFAULT',
                'in-ext-md': 'DEFAULT',
                'neeur-scp-sd': 'DEFAULT',
                'neeur-scp-md': 'DEFAULT',
                'au-scp-sd': 'AUST',
                'au-scp-md': 'AUST',
                'th-scp-sd': 'DEFAULT',
                'fr-ext': 'DEFAULT',
                'br-ext': 'DEFAULT',
                'pl-scp-sd': 'DEFAULT',
                'mx-scp-sd': 'DEFAULT',
                'mx-scp-md': 'DEFAULT'
            }
        };
    }

    public get currencyPattern() {
        return this.currencyPattern;
    }
    private _$currencyPattern: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public get currencyPattern$(): Observable<any> {
        return this._$currencyPattern.asObservable();
    }

    private _$portalPreferences: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public get portalPreferences$(): Observable<any> {
        return this._$portalPreferences.asObservable();
    }
    

    constructor(
        private _restApiService: RestApiService,
        private _configurationService: ConfigurationService,
        private _localizeRouterService: LocalizeRouterService,
        private _translateService: TranslateService,
        private _router: Router
    ) {}

    getPortalConfiguration(): Promise<any> {
        const _url = `${environment.APIEndpoint}api/v1/portal`;
        // const _url = 'assets/mock/' + 'portal_config' + '.json';
        return this._restApiService
            .requestGET(_url)
            .then((config) => {
                return config;
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    }

    checkForMatchingPortalConfig(id: any, configuration: any): Promise<any> {
        if (!id || id === '') {
            return Promise.reject('No id in method call.');
        }
        if (!configuration) {
            return Promise.reject('Portal configuration missing.');
        }

        const _matchingPortals = configuration.filter((portal) => {
            return portal.toLowerCase() === this.getPortalIdWithUnderscore(id).toLowerCase();
        });
        if (_matchingPortals.length > 0) {
            return Promise.resolve(id);
        } else {
            const translatedPath: any = this._localizeRouterService.translateRoute('/' + 'page-not-found');
            this._router.navigate([translatedPath], { skipLocationChange: true });
            return Promise.reject('No portal-configuration found with id ' + id + '.');
        }
    }

    loadPortalPreferences(portalId: string): Promise<PortalPreferencesInterface> {
        const _portalId = this.getPortalIdWithUnderscore(portalId);
        const _url = `${environment.APIEndpoint}api/v1/portal/${_portalId}`;
        if (this._$portalPreferences.getValue()) {
            return Promise.resolve(this._$portalPreferences.getValue());
        }

        return this._restApiService
            .requestGET(_url)
            .then((config: PortalPreferencesInterface) => {
                this._configurationService.setPortalPreferences(config);
                if (config.currencyFormat) {
                    this._$currencyPattern.next(config.currencyFormat);
                }
                else {
                    this._$currencyPattern.next('#.##0,00 " $"'); 
                }
                this._$portalPreferences.next(config);
                return config;
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    }

    loadLegalfile(pageName: string, language: string): Promise<any> {
        const _url = `${environment.APIEndpoint}api/v1/Portal/terms/${pageName}/${language}`;
        return this._restApiService
            .requestGET(_url)
            .then((config) => {
                return config;
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    }

    getPortalInfoByPageType(pageType: string, property: string): string {
        const _infos = {
            revocation: {
                breadcrumbProperty: 'labelRevocation',
                context: 'FILES_REVOCATION',
                filePrefix: 'WIDERRUFSBELEHRUNG_'
            },
            terms: {
                breadcrumbProperty: 'labelTermsConditions',
                context: 'FILES_AGB',
                filePrefix: 'AGB_'
            },
            disclaimer: {
                breadcrumbProperty: 'labelDisclaimer',
                context: 'FILES_DISCLAIMER',
                filePrefix: 'DISCLAIMER_'
            }
        };
        return _infos[pageType][property];
    }

    public cleanUpJson(json: any): string {
        const _jsn = JSON.stringify(json);
        const _cleanJson = _jsn
            .replace(/<\s*(\w+).*?>/gm, '<$1>')
            .replace(/<(\/)?\s*font\s*([^\s]*?)\s*>/gm, '')
            .replace(/<[^/>][^>]*><\/[^>]+>/gi, '')
            .replace(/<p><b><span>&nbsp;<\/span><\/b><span><\/span><\/p>/gi, '')
            .replace(/<span>&nbsp;<\/span>/gi, '')
            .replace(/<span><\/span>/gi, '')
            .replace(/<p><span>\* \* \* \* \*<\/span><\/p>/g, '')
            .replace(/<p><a><b><span>&nbsp;<\/span><\/b><\/a><\/p>/gi, '')
            .replace(/<p><b><span>&nbsp;<\/span><\/b><\/p>/gi, '')
            .replace(/<p><\/p>/gi, '')
            .replace(/<p><b><span>&nbsp;<\/span><\/b><\/p>/gi, '')
            .replace(/[\uf0b7]/g, '');
        return JSON.parse(_cleanJson);
    }

    public getPortalLanguage(preferences: PortalPreferencesInterface): string {
        let _lang;
        let _langToUse = null;
        if (preferences) {
            const _defaultLanguage = preferences.defaultLanguage;
            const _availableLanguages = preferences.availableLanguages;
            const _langFromStorage = this.getStoredLanguage(preferences.name); // JSON.parse(sessionStorage.getItem('portalLang'))?.lang;
            if (_langFromStorage) {
                _langToUse = _langFromStorage;
            }

            if (_langToUse) {
                _lang = _availableLanguages.filter((lang) => lang.code.toLowerCase() === _langToUse.toLowerCase())[0]?.code;
            }
            _lang = _lang ? _lang : _defaultLanguage.split(';')[0];
            this._configurationService.setPortalLanguage(_lang, this.getPortalIdWithDash(preferences.name));
        }
        return _lang;
    }

    public getPortalCountry(preferences: PortalPreferencesInterface): string {
        const _defaultCountry = preferences.defaultCountry;
        return _defaultCountry;
    }
    public getPortalDefaultLanguage(preferences: PortalPreferencesInterface): string {
        const _defaultLanguage = preferences.defaultLanguage;
        return _defaultLanguage;
    }
    public getPortalDateFormat(preferences: PortalPreferencesInterface): string {
        const _dateFormat = preferences.dateFormat;
        return _dateFormat;
    }

    public getPortalImprintURL(preferences: PortalPreferencesInterface): string {
        const _imprintURL = preferences?.imprintUrl;
        return _imprintURL;
    }

    private checkIfPortalIsInStorage(portalName: any) {
        const _sessionValue = sessionStorage.getItem('portalLang');
        const _portalId = this.getPortalIdWithDash(portalName);
        if (_sessionValue) {
            return JSON.parse(_sessionValue).portalId === _portalId;
        }
        return false;
    }

    public getPortalIdWithDash(portalName: string): string {
        return portalName.split('_').join('-').toLowerCase();
    }

    public getPortalIdWithUnderscore(portalName: string): string {
        if (portalName && portalName !== null) {
            return portalName.split('-').join('_').toUpperCase();
        }
    }

    public getQueryParamArrayFromString(_parameterUrl) {
        return _parameterUrl
            ? (/^[?#]/.test(_parameterUrl) ? _parameterUrl.slice(1) : _parameterUrl).split(/\?|&/).reduce((params, param) => {
                  const [key, value] = param.split('=');
                  params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
                  return params;
              }, {})
            : {};
    }

    public checkForDefaultLanguageFromParamOrPreferences(params: Params, portalId: string, prefrences: PortalPreferencesInterface) {
        const _languageFromParams = this._getLangFromParams(params);
        if (
            !this.getStoredLanguage(portalId) ||
            (_languageFromParams && this.getStoredLanguage(portalId).toLowerCase() !== _languageFromParams.toLowerCase())
        ) {
            const _languageFromParamsOrDefault = this._checkIfLanguageIsAvailableInPortalOrReturnDefault(_languageFromParams, prefrences);

            if (_languageFromParamsOrDefault) {
                this._configurationService.setPortalLanguage(_languageFromParamsOrDefault, portalId);
            }
        }
    }

    private _checkIfLanguageIsAvailableInPortalOrReturnDefault(_languageFromParams: any, prefrences: PortalPreferencesInterface) {
        if (_languageFromParams) {
            const _matchFromPreferences = prefrences.availableLanguages.filter(
                (lang) => lang.code.toLowerCase() === _languageFromParams.toLowerCase()
            );
            if (_matchFromPreferences.length > 0) {
                return _matchFromPreferences[0]?.code;
            }
        }
        return prefrences.defaultLanguage.split(';')[0];
    }

    private _getLangFromParams(params: Params) {
        let _lang = null;
        if (params && params['L'] && params['L'] !== '') {
            _lang = params['L'];
        }

        if (params && params['url'] && params['url'] !== '') {
            const _languageFromDeeplink = this._checkForLanguageInDeepLink(params['url']);
            if (_languageFromDeeplink && _languageFromDeeplink !== '') {
                _lang = _languageFromDeeplink;
            }
        }

        return _lang;
    }

    private _checkForLanguageInDeepLink(deeplink: string) {
        const _paramArray = this.getQueryParamArrayFromString(deeplink);
        if (_paramArray['L']) {
            return _paramArray['L'];
        }
        return null;
    }

    public getStoredLanguage(portalName: string): string {
        let _lang = null;
        if (sessionStorage && this.checkIfPortalIsInStorage(portalName)) {
            const _langFromStorage = JSON.parse(sessionStorage.getItem('portalLang'))?.lang;
            if (_langFromStorage) {
                _lang = _langFromStorage;
            }
        }
        return _lang;
    }
}
